/**
 * The converter dropzone: drag, browse, or paste.
 *
 * It classifies what arrived and hands bytes to the worker. It does not parse
 * anything — "including just splitting SRT to count cues" is on the forbidden
 * list in `contracts/adapter.md` §11, and the classification below is routing
 * (which pipeline does this file enter) rather than parsing (what is in it).
 *
 * The one thing it must get right on its own is the **video container path**.
 * `ffmpeg.wasm` is an order of magnitude larger than the rest of the app and
 * has a real memory ceiling on a phone, so it is fetched on the first
 * container drop and never on page load — and while it downloads, the user is
 * told what is downloading rather than watching a spinner.
 */

import { on, qs, setData } from './dom.ts';

export type DropKind = 'subtitle' | 'archive' | 'container' | 'other-video' | 'audio' | 'unknown';

/** Read formats, from docs/PRD.md. Extension-keyed: routing, not detection. */
const SUBTITLE_EXT = new Set([
  'srt',
  'vtt',
  'ass',
  'ssa',
  'smi',
  'sami',
  'sub',
  'mpl2',
  'txt',
  'lrc',
  'sbv',
  'json',
  // TTML (contract 1.11.0). `.xml` is routed here too: the worker's sniff reads it as TTML
  // when it starts with <tt>, and refuses any other XML by name.
  'ttml',
  'dfxp',
  'xml',
  // SCC (contract 1.13.0): broadcast captions as hex words, read by the worker's CEA-608 decoder.
  'scc',
]);
/**
 * Videos a page hands to its readers. MKV and WebM, and MP4's family (MP4, MOV, M4V, and 3GP, 3G2
 * and F4V, which are MP4 inside), open in the subtitle reader (src/worker/adapter/demux.ts) and in
 * the audio copy (mediabunny). MPEG-TS (.ts, and a camcorder's or a Blu-ray's .mts and .m2ts) and
 * Ogg video open in the audio copy only, and AVI in neither: the subtitle reader refuses AVI and
 * .ts with a sentence of its own, and the other three with B5_VIDEO_FORMAT (lib/video-flow.ts),
 * while a page that takes the sound still tries it.
 */
const CONTAINER_EXT = new Set([
  'mkv',
  'mp4',
  'avi',
  'mov',
  'ts',
  'webm',
  'm4v',
  '3gp',
  '3g2',
  'f4v',
  'mts',
  'm2ts',
  'ogv',
]);
/** Of those, the ones whose sound the audio copy reads and whose subtitles the reader can't. */
const SOUND_ONLY_EXT = new Set(['mts', 'm2ts', 'ogv']);
/**
 * Videos in a container nothing here opens: Windows Media, Flash, MPEG program streams and DVD
 * video, MXF, RealMedia, DivX. They get B5_VIDEO_FORMAT, which names the format and the ones that
 * work; they reached "We can't read this file type" before (live, 2026-10-19).
 */
const OTHER_VIDEO_EXT = new Set([
  'wmv',
  'asf',
  'flv',
  'mpg',
  'mpeg',
  'm2v',
  'vob',
  'mxf',
  'rm',
  'rmvb',
  'divx',
]);
export const AUDIO_EXT: ReadonlySet<string> = new Set([
  'mp3',
  'wav',
  'aac',
  'ogg',
  'flac',
  'm4a',
  'opus',
  'oga',
]);
const ARCHIVE_EXT = new Set(['zip']);

export function extensionOf(name: string): string {
  const dot = name.lastIndexOf('.');
  return dot < 0 ? '' : name.slice(dot + 1).toLowerCase();
}

/**
 * The format a video banner names, from the file's extension: `clip.wmv` is `WMV`. For the page
 * only: the usage counter counts such a file as `video` and never sees the extension.
 */
export function videoTypeName(name: string): string {
  return extensionOf(name).toUpperCase();
}

/** True for a video whose sound a page can copy out but whose subtitle tracks it can't read. */
export function soundOnlyVideo(name: string): boolean {
  return SOUND_ONLY_EXT.has(extensionOf(name));
}

export function classify(file: File): DropKind {
  const ext = extensionOf(file.name);
  if (ARCHIVE_EXT.has(ext)) return 'archive';
  if (SUBTITLE_EXT.has(ext)) return 'subtitle';
  if (CONTAINER_EXT.has(ext)) return 'container';
  // Before the MIME fallback: Windows reports a .wmv as video/x-ms-wmv, which would send it to
  // a reader that can only refuse it.
  if (OTHER_VIDEO_EXT.has(ext)) return 'other-video';
  if (AUDIO_EXT.has(ext)) return 'audio';
  // MIME is the fallback, not the primary: browsers routinely report
  // application/octet-stream for .ass and .srt, and an in-app browser will
  // report text/plain for a .mkv.
  if (file.type.startsWith('audio/')) return 'audio';
  if (file.type.startsWith('video/')) return 'container';
  if (file.type.startsWith('text/')) return 'subtitle';
  return 'unknown';
}

export interface DropzoneCallbacks {
  onFiles: (files: File[]) => void;
  /** Pasted text that is subtitle content rather than a file. */
  onText?: (text: string) => void;
}

export interface DropzoneHandle {
  destroy: () => void;
  /** Show or clear the "downloading the video reader" line. */
  setLoaderStatus: (message: string | null) => void;
}

export function mountDropzone(host: HTMLElement, callbacks: DropzoneCallbacks): DropzoneHandle {
  const zone = qs<HTMLElement>('[data-dropzone]', host) ?? host;
  const input = qs<HTMLInputElement>('[data-dropzone-input]', host);
  const status = qs<HTMLElement>('[data-ffmpeg-status]', host);
  const offs: (() => void)[] = [];

  const stop = (ev: Event) => {
    ev.preventDefault();
    ev.stopPropagation();
  };

  offs.push(
    on(zone, 'dragover', (ev) => {
      stop(ev);
      setData(zone, 'dragover', 'true');
    }),
    on(zone, 'dragleave', (ev) => {
      stop(ev);
      setData(zone, 'dragover', null);
    }),
    on(zone, 'drop', (ev) => {
      stop(ev);
      setData(zone, 'dragover', null);
      const transfer = (ev as DragEvent).dataTransfer;
      if (!transfer) return;
      const files = Array.from(transfer.files);
      if (files.length > 0) callbacks.onFiles(files);
    }),
  );

  // The whole document swallows drags too, so a near-miss drop does not
  // navigate the browser to the file — which is the single most annoying
  // failure mode of a drop target and costs three lines to prevent.
  offs.push(on(document, 'dragover', stop), on(document, 'drop', stop));

  if (input) {
    offs.push(
      on(input, 'change', () => {
        const files = Array.from(input.files ?? []);
        if (files.length > 0) callbacks.onFiles(files);
        // Reset so re-picking the same file fires again.
        input.value = '';
      }),
    );
  }

  // "Drag, browse, or paste" — paste covers both a copied file and copied
  // subtitle text, and the second is how people move a cue list out of a chat
  // window.
  offs.push(
    on(document, 'paste', (ev) => {
      const clip = (ev as ClipboardEvent).clipboardData;
      if (!clip) return;
      const files = Array.from(clip.files ?? []);
      if (files.length > 0) {
        ev.preventDefault();
        callbacks.onFiles(files);
        return;
      }
      const target = ev.target;
      // Do not steal a paste aimed at a text field.
      if (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement) return;
      const text = clip.getData('text/plain');
      if (text && text.length > 40 && callbacks.onText) {
        ev.preventDefault();
        callbacks.onText(text);
      }
    }),
  );

  return {
    destroy() {
      for (const off of offs) off();
    },
    setLoaderStatus(message) {
      if (!status) return;
      status.textContent = message ?? '';
      status.hidden = message === null;
    },
  };
}
